import * as api from '../../swc-app-api/api'
import * as configuration from '../../swc-app-api/configuration'
import axios from './axios'
import { Cookies, SessionStorage } from 'quasar'

let timeout = null
const navigationContextRequests = new Map()
let userNodeRolesRequest = null
let userNodeRolesLoaded = false

function syncLoadedState(commit, state, nextState = {}) {
  const isCommonLoaded = nextState.isCommonLoaded ?? state.isCommonLoaded
  const isRouteLoaded = nextState.isRouteLoaded ?? state.isRouteLoaded
  commit('SET_LOADED', isCommonLoaded && isRouteLoaded)
}

function normalizeNodeId(nodeId) {
  if (nodeId === undefined || nodeId === null || nodeId === '') {
    return null
  }
  return Number(nodeId)
}

function requireNodeId(nodeId) {
  const id = normalizeNodeId(nodeId)
  if (!Number.isInteger(id)) {
    throw new TypeError('nodeId must be an integer')
  }
  return id
}

function flattenNodes(nodes) {
  return nodes.flatMap(node => {
    if (node.children_detail.length === 0) { return node }
    return flattenNodes(node.children_detail)
  })
}

// ツリーを走査して指定 id のノード(中間/葉を問わない)を返す
function findNodeInTree(nodes, id) {
  for (const node of nodes) {
    if (node.id === id) { return node }
    const found = findNodeInTree(node.children_detail || [], id)
    if (found) { return found }
  }
  return null
}

function determineDefaultNode(userInfo, nodes) {
  const flatNodes = flattenNodes(nodes)
  const dataNodes = flatNodes.filter(n => n.is_data)
  const favPage = userInfo.favorite_page
  if (favPage && favPage.includes('/node/')) {
    const nodeId = favPage.replace(/[^0-9]/g, '')
    const node = flatNodes.find(n => n.id === nodeId)
    if (node) { return node }
  }
  if (dataNodes.length > 0) { return dataNodes[0] }
  if (flatNodes.length > 0) { return flatNodes[0] }
  return {}
}

export function setApiClients({commit, state}) {
  // トークン更新後も最新のアクセストークンを送るため、値ではなく取得関数を渡す
  const config = new configuration.Configuration({
    accessToken: () => localStorage.getItem('auth/ACCESS_TOKEN')
  })
  const apiClient = {
    ...api.MasterApiFactory(config, undefined, axios),
    ...api.UserinfoApiFactory(config, undefined, axios),
    ...api.NodesApiFactory(config, undefined, axios),
    ...api.UserApiFactory(config, undefined, axios)
    // ...api.DatasetsApiFactory(config, undefined, axios),
    // ...api.ResampledDatasetsApiFactory(config, undefined, axios),
    // ...api.SitesApiFactory(config, undefined, axios),
  }
  commit('SET_API_CLIENT', apiClient)
}

export function startNavigationSession({commit, state}) {
  const generation = state.navigationSessionGeneration + 1
  navigationContextRequests.clear()
  commit('SET_NAVIGATION_SESSION_GENERATION', generation)
  commit('RESET_NODE_NAVIGATION')
  return generation
}

export function loadCommonData({commit, dispatch, state}) {
  dispatch('startNavigationSession')
  if (!state.apiClient) {
    setApiClients({commit, state})
  }
  const apiClient = state.apiClient

  return new Promise((resolve, reject) => {
    Promise.all([
      apiClient.userinfoRetrieve(), // ユーザー情報
      apiClient.masterOrganizationsList(), // 組織
    ])
      .then((values) => {
        const userInfo = values[0].data
        const organizations = values[1].data

        commit('SET_USERINFO', userInfo)
        // Google analytics(GTM)送信用
        localStorage.setItem('swac_user_id', userInfo.id)
        localStorage.setItem('organization_code', userInfo.organization_code)
        commit('SET_ORGANIZATIONS', organizations)

        commit('SET_COMMON_LOADED', true)
        syncLoadedState(commit, state, {isCommonLoaded: true})

        resolve()
      })
      .catch((e) => {
        reject()
        console.error(e)
      })
  })
}

export function ensureUserNodeRoles({commit, state}) {
  if (!state.apiClient) {
    setApiClients({commit, state})
  }
  if (userNodeRolesLoaded) {
    return Promise.resolve(state.userRoles)
  }
  if (userNodeRolesRequest) {
    return userNodeRolesRequest
  }
  userNodeRolesRequest = state.apiClient
    .masterUserNodeRolesList()
    .then((response) => {
      commit('SET_USERROLES', response.data)
      userNodeRolesLoaded = true
      return response.data
    })
    .catch((e) => {
      console.error(e)
      throw e
    })
    .finally(() => {
      userNodeRolesRequest = null
    })
  return userNodeRolesRequest
}

export function loadRouteData({commit, dispatch, state}, options = {}) {
  if (!state.apiClient) {
    setApiClients({commit, state})
  }
  const apiClient = state.apiClient
  const scope = options.scope ?? 'full'
  const loadNodes = options.loadNodes !== false
  const nodeId = normalizeNodeId(options.nodeId)
  // light=true のときはログイン初期化用の軽量ノード(付帯情報なし)を取得する
  const light = options.light === true

  if (!loadNodes && nodeId === null) {
    commit('SET_ROUTE_INIT_SCOPE', scope)
    commit('SET_ROUTE_LOADED', true)
    syncLoadedState(commit, state, {isRouteLoaded: true})
    return Promise.resolve()
  }

  if (!loadNodes && nodeId !== null) {
    return new Promise((resolve, reject) => {
      apiClient.masterNodesRetrieve(nodeId)
        .then((response) => {
          const currentNode = response.data
          commit('SET_DEFAULT_NODE', currentNode)
          commit('SET_ROUTE_INIT_SCOPE', scope)
          commit('SET_ROUTE_LOADED', true)
          syncLoadedState(commit, state, {isRouteLoaded: true})
          resolve()
        })
        .catch((e) => {
          reject(e)
          console.error(e)
        })
    })
  }

  return new Promise((resolve, reject) => {
    const nodesRequest = light ? apiClient.masterLoginNodesList() : apiClient.masterSimpleNodesList()
    nodesRequest
      .then(async (response) => {
        const nodes = response.data
        SessionStorage.set('nodes', JSON.stringify(flattenNodes(nodes)))  // グラフィック関数で使用
        const defualtNode = determineDefaultNode(state.userInfo, nodes)
        commit('SET_DEFAULT_NODE', defualtNode)
        if (light && nodeId !== null) {
          await dispatch('ensureNodeNavigationContext', nodeId)
        }
        commit('SET_NODES', nodes)
        commit('SET_ROUTE_INIT_SCOPE', scope)
        commit('SET_ROUTE_LOADED', true)
        syncLoadedState(commit, state, {isRouteLoaded: true})
        resolve()
      })
      .catch((e) => {
        reject()
        console.error(e)
      })
  })
}

export function getData({dispatch}) {
  return dispatch('loadCommonData')
    .then(() => dispatch('loadRouteData', {scope: 'full', loadNodes: true}))
}

export function ensureNodeNavigationContext({commit, state}, nodeId) {
  const id = requireNodeId(nodeId)
  if (state.nodeNavigationStatus[id] === 'loaded' && state.nodeNavigationContexts[id]) {
    return Promise.resolve(state.nodeNavigationContexts[id])
  }
  if (!state.apiClient) {
    setApiClients({commit, state})
  }

  const generation = state.navigationSessionGeneration
  const requestKey = `${generation}:${id}`
  const currentRequest = navigationContextRequests.get(requestKey)
  if (currentRequest) {
    return currentRequest
  }

  commit('SET_NODE_NAVIGATION_STATUS', {nodeId: id, status: 'loading'})
  const request = state.apiClient.masterNodesNavigationContextRetrieve(id)
    .then((response) => {
      if (state.navigationSessionGeneration !== generation || navigationContextRequests.get(requestKey) !== request) {
        return null
      }
      commit('SET_NODE_NAVIGATION_CONTEXT', {nodeId: id, context: response.data})
      commit('SET_NODE_NAVIGATION_STATUS', {nodeId: id, status: 'loaded'})
      return response.data
    })
    .catch((error) => {
      if (state.navigationSessionGeneration !== generation || navigationContextRequests.get(requestKey) !== request) {
        return null
      }
      commit('SET_NODE_NAVIGATION_STATUS', {nodeId: id, status: 'error'})
      throw error
    })
    .finally(() => {
      if (navigationContextRequests.get(requestKey) === request) {
        navigationContextRequests.delete(requestKey)
      }
    })
  navigationContextRequests.set(requestKey, request)
  return request
}

export async function prepareNodeNavigation({commit, dispatch, state}, nodeId) {
  const id = requireNodeId(nodeId)
  const sequence = state.nodeNavigationSequence + 1
  const sessionGeneration = state.navigationSessionGeneration
  commit('SET_NODE_NAVIGATION_SEQUENCE', sequence)
  commit('SET_PENDING_NODE_ID', id)

  let context
  try {
    context = await dispatch('ensureNodeNavigationContext', id)
  } catch (error) {
    if (state.navigationSessionGeneration === sessionGeneration &&
        state.nodeNavigationSequence === sequence &&
        state.pendingNodeId === id) {
      commit('SET_PENDING_NODE_ID', null)
    }
    throw error
  }
  if (context === null ||
      state.navigationSessionGeneration !== sessionGeneration ||
      state.nodeNavigationSequence !== sequence ||
      state.pendingNodeId !== id) {
    return null
  }
  return {nodeId: id, context, sequence, sessionGeneration}
}

export function commitNodeNavigation({commit, state}, {candidate, routeNodeId}) {
  if (!candidate) {
    return false
  }
  const id = normalizeNodeId(routeNodeId)
  if (id !== candidate.nodeId ||
      state.navigationSessionGeneration !== candidate.sessionGeneration ||
      state.nodeNavigationSequence !== candidate.sequence ||
      state.pendingNodeId !== candidate.nodeId) {
    return false
  }
  commit('SET_NODEID', candidate.nodeId)
  commit('SET_PENDING_NODE_ID', null)
  return true
}

export function invalidateNodeNavigationContext({commit, state}, nodeId) {
  const id = requireNodeId(nodeId)
  const nodesById = new Map(flattenNodes(state.nodes).map((node) => [node.id, node]))
  const nodeIds = []
  let currentId = id
  while (currentId !== null && currentId !== undefined && !nodeIds.includes(currentId)) {
    nodeIds.push(currentId)
    currentId = nodesById.get(currentId)?.parent_id
  }
  for (const requestKey of navigationContextRequests.keys()) {
    const requestNodeId = Number(requestKey.split(':')[1])
    if (nodeIds.includes(requestNodeId)) {
      navigationContextRequests.delete(requestKey)
    }
  }
  commit('REMOVE_NODE_NAVIGATION_CONTEXTS', nodeIds)
}

export function destroyData({commit, state}) {
  const generation = state.navigationSessionGeneration + 1
  navigationContextRequests.clear()
  userNodeRolesRequest = null
  userNodeRolesLoaded = false
  commit('SET_NAVIGATION_SESSION_GENERATION', generation)
  commit('RESET_NODE_NAVIGATION')
  commit('SET_USERINFO', {})
  commit('SET_NODES', [])
  commit('SET_NODEID', null)
  commit('SET_DEFAULT_NODE', null)
  commit('SET_ORGANIZATIONS', [])
  commit('SET_USERROLES', [])
  commit('SET_COMMON_LOADED', false)
  commit('SET_ROUTE_LOADED', false)
  commit('SET_ROUTE_INIT_SCOPE', null)
  commit('SET_LOADED', false)
  commit('SET_API_CLIENT', null)
  commit('SET_DISABLE', false)
}

export function updateFavoritePage({commit, state}, favoritePage) {
  const apiClient = state.apiClient
  const payload = {
    favorite_page: favoritePage
  }
  return new Promise((resolve, reject) => {
    apiClient
      .masterUsersPartialUpdate(state.userInfo.id, payload)
      .then((response) => {
        const {favorite_page} = response.data
        commit('SET_USERINFO', {...state.userInfo, favorite_page})
        resolve()
      })
      .catch((e) => {
        reject()
        console.error(e)
      })
  })
}

export function changeDisable({commit}, isDisable) {
  if(timeout !== null) clearTimeout(timeout)
  timeout = setTimeout(() => {
    commit('SET_DISABLE', false)
    timeout = null
  },1000 * 60);
  commit('SET_DISABLE', isDisable)
}
